// Canvas 2D（PDF 出力・エクスポート画像）向けの Markdown 行レイアウト計算。
//
// 画面表示（MarkdownTextView / markdown-text.ts）と同じパース結果・スタイル定数を使い、
// 見出しの倍率 + 太字、箇条書きのぶら下げインデント + マーカーを Canvas の描画座標へ
// 変換する。行送りは画面の line-height（1.5）と同じ比率（STICKY_LINE_HEIGHT_RATIO）を使う。
//
// 文字幅の計測は関数注入（measure）とし、Canvas 2D の無い環境（jsdom のユニットテスト）
// でもフェイク計測で決定的に検証できるようにする。

import {
  BULLET_INDENT_EM,
  HEADING_FONT_SCALE,
  markdownLineDisplayTextFor,
  parseMarkdownTextLines,
} from "./markdown-text";
import { STICKY_LINE_HEIGHT_RATIO } from "./sticky-font-fit";

export declare namespace layoutMarkdownTextForCanvas {
  export type { Anchor, Measure, Options, Result, Segment };
}

/**
 * 本文テキストを Canvas 描画用のセグメント列へレイアウトする。
 *
 * - 見出し行: フォント = 基準 × HEADING_FONT_SCALE、太字。アンカーは align に従う。
 * - 箇条書き行: マーカー付きテキストをぶら下げインデントで折り返す。アンカーは align に
 *   依らず常に左（インデント構造が中央 / 右寄せと両立しないため。画面表示と同じ仕様）。
 * - plain 行: 基準フォント。アンカーは align に従う。
 * - 空行: セグメントを出さず、基準フォント 1 行分の高さだけ送る。
 *
 * 折り返しは既存の PDF 出力（旧 wrapTextToLines）と同じ 1 文字単位の詰め込み方式で、
 * 1 文字も入らない幅でもその 1 文字は必ず出力する（無限ループ防止）。その場合は
 * overflowsWidth を true にし、付箋のフォント自動フィットが「収まらない」と判定できる
 * ようにする。
 */
export function layoutMarkdownTextForCanvas(options: Options): Result {
  const { align, baseFontPx, maxWidth, measure, text } = options;
  const segments: Segment[] = [];
  let cursorY = 0;
  let overflowsWidth = false;
  for (const line of parseMarkdownTextLines(text)) {
    if (line.kind === "blank") {
      cursorY += baseFontPx * STICKY_LINE_HEIGHT_RATIO;
      continue;
    }
    const bold = line.kind === "heading";
    const fontPx =
      line.kind === "heading" ? baseFontPx * HEADING_FONT_SCALE[line.level] : baseFontPx;
    const lineHeight = fontPx * STICKY_LINE_HEIGHT_RATIO;
    // 箇条書きのインデント（px）。画面表示の paddingLeft / textIndent（em 指定）と同じ比率。
    // 1 行目はぶら下げ（負の textIndent）相当でマーカー位置から、2 行目以降は本文位置から始まる。
    const hangingPx = line.kind === "bullet" ? BULLET_INDENT_EM * fontPx : 0;
    const firstLineIndentPx = line.kind === "bullet" ? line.level * BULLET_INDENT_EM * fontPx : 0;
    const wrappedIndentPx = line.kind === "bullet" ? firstLineIndentPx + hangingPx : 0;
    const lineAnchor: Anchor = line.kind === "bullet" ? "left" : align;
    const wrapped = wrapLineText({
      bold,
      firstLineWidth: maxWidth - firstLineIndentPx,
      fontPx,
      measure,
      // 画面表示（MarkdownTextView -> LinkifiedText）と同じく `[label](url)` は表示テキストへ
      // 縮約したうえで折り返し・幅計算する（PDF・エクスポート画像に raw 記法が出るのを防ぐ）。
      text: markdownLineDisplayTextFor(line),
      wrappedWidth: maxWidth - wrappedIndentPx,
    });
    wrapped.forEach((segmentText, index) => {
      const indentPx = index === 0 ? firstLineIndentPx : wrappedIndentPx;
      if (measure(segmentText, fontPx, bold) > Math.max(0, maxWidth - indentPx)) {
        overflowsWidth = true;
      }
      segments.push({
        anchor: lineAnchor,
        baselineY: cursorY + fontPx,
        bold,
        fontPx,
        text: segmentText,
        x: lineAnchor === "center" ? maxWidth / 2 : lineAnchor === "right" ? maxWidth : indentPx,
      });
      cursorY += lineHeight;
    });
  }
  return { overflowsWidth, segments, totalHeight: cursorY };
}

//------------------------------------------------------------------------------
// Helpers
//------------------------------------------------------------------------------

type Anchor = "left" | "center" | "right";

/** 指定フォントでの文字列幅（px）を返す計測関数。Canvas 2D の measureText を包んで注入する。 */
type Measure = (text: string, fontPx: number, bold: boolean) => number;

interface Options {
  /** 見出し・plain 行の文字寄せ（箇条書き行は常に左寄せ）。 */
  readonly align: Anchor;
  /** 基準フォントサイズ（px。スケール適用後の値を渡す）。 */
  readonly baseFontPx: number;
  /** 描画可能幅（px）。アンカー・折り返しの基準になる。 */
  readonly maxWidth: number;
  readonly measure: Measure;
  /** 生の Markdown テキスト（object.text）。 */
  readonly text: string;
}

interface Segment {
  /** ctx.textAlign へ設定する値。 */
  readonly anchor: Anchor;
  /** 描画領域の上端からのベースライン位置（px）。 */
  readonly baselineY: number;
  readonly bold: boolean;
  readonly fontPx: number;
  readonly text: string;
  /** 描画領域の左端からのアンカー X（px）。 */
  readonly x: number;
}

interface Result {
  /** 折り返してもいずれかのセグメントが幅へ収まらない場合 true（フィット判定用）。 */
  readonly overflowsWidth: boolean;
  readonly segments: readonly Segment[];
  /** 全行の行送り合計（px）。フィット判定の高さ比較に使う。 */
  readonly totalHeight: number;
}

/**
 * 1 論理行を幅に収まるよう 1 文字単位で折り返す（日本語 / 英語混在向けの詰め込み方式。
 * 旧 wrapTextToLines と同じ判定）。1 行目とそれ以降で利用可能幅を分けられるようにし、
 * 箇条書きのぶら下げインデントを表現する。
 */
function wrapLineText(params: {
  readonly bold: boolean;
  readonly firstLineWidth: number;
  readonly fontPx: number;
  readonly measure: Measure;
  readonly text: string;
  readonly wrappedWidth: number;
}): string[] {
  const { bold, firstLineWidth, fontPx, measure, text, wrappedWidth } = params;
  const wrapped: string[] = [];
  let current = "";
  let availableWidth = firstLineWidth;
  for (const character of text) {
    const candidate = current + character;
    if (measure(candidate, fontPx, bold) > availableWidth && current.length > 0) {
      wrapped.push(current);
      current = character;
      availableWidth = wrappedWidth;
    } else {
      current = candidate;
    }
  }
  wrapped.push(current);
  return wrapped;
}
